import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Select, Skeleton, Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listTemplateOverridesOrgs } from 'src/modules/SuperAdmin/store/actions';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { listPractitionersFromOrg } from 'src/store/actions/practitioner.action';
import { classNames } from 'src/utilities';

const TemplatesAdminSource = ({
  setSelectedTemplatesDataSource,
  selectedTemplatesDataSource,
  refreshTrigger,
}) => {
  const dispatch = useDispatch();

  const [organizationDropdown, setOrganizationDropdown] = useState([]);
  const [practitionerDropdown, setPractitionerDropdown] = useState([]);
  const [templateDropdown, setTemplateDropdown] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedPractitioners, setSelectedPractitioners] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  const SummaryLoading = () => (
    <div className="settings-skeleton">
      <Skeleton paragraph={{ rows: 1, style: { height: 16 } }} />
    </div>
  );

  const columns = [
    {
      title: 'Template Name',
      render: (text, record) => (
        <div className="flex justify-between items-center">
          <span>{record.name}</span>
          <Tooltip
            title={
              <div
                className={`overflow-auto cursor-pointer ${record.body.sections && record.body.sections.length > 10 ? 'max-h-[40rem]' : 'h-fit'} w-[15rem]`}
              >
                {record.body.sections && record.body.sections.length > 0 ? (
                  record.body.sections.map((section, index) => (
                    <p key={index}>
                      {section.name} <SummaryLoading />
                    </p>
                  ))
                ) : (
                  <p>No template sections</p>
                )}
              </div>
            }
            placement={'right'}
            arrow={false}
            overlayStyle={
              record.body.sections && record.body.sections.length > 10
                ? { position: 'fixed', top: '1.25rem', bottom: '3rem' }
                : {}
            }
          >
            <span style={{ cursor: 'default' }} className="text-gray-400">
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  const { allPractitionerNames, allPractitionerNamesLoading } = useSelector(
    (state) => state?.practitionerState
  );
  const { templateOverridesList } = useSelector(
    (state) => state.templateOverridesState
  );
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let orgData = [];
      getActiveOrganizationsList.forEach((key) => {
        orgData.push({
          label: key?.org_name,
          value: key?.org_uuid,
        });
      });
      setOrganizationDropdown(orgData);
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    if (selectedOrganizations.length > 0) {
      dispatch(listPractitionersFromOrg(selectedOrganizations));
      dispatch(listTemplateOverridesOrgs(selectedOrganizations));
    } else {
      setPractitionerDropdown([]);
      setTemplateDropdown([]);
    }
  }, [selectedOrganizations, refreshTrigger, dispatch]);

  useEffect(() => {
    if (templateOverridesList.length === 0) {
      setTemplateDropdown([]);
    }
  }, [templateOverridesList]);

  useEffect(() => {
    if (templateOverridesList.length > 0 && selectedOrganizations.length > 0) {
      setTemplateDropdown(
        templateOverridesList.map((item) => ({
          label: item.name,
          value: item.template_overrides_id,
          body: item.body,
        }))
      );
    }
  }, [templateOverridesList, selectedOrganizations]);

  useEffect(() => {
    if (allPractitionerNamesLoading === false) {
      if (selectedOrganizations.length > 0) {
        setPractitionerDropdown(
          allPractitionerNames.map((item) => ({
            label: item.practitioner_name,
            value: item.practitioner_id,
          }))
        );
      }
    }
  }, [
    allPractitionerNames,
    allPractitionerNamesLoading,
    selectedOrganizations.length,
  ]);

  useEffect(() => {
    if (
      selectedPractitioners.length === 0 &&
      templateOverridesList.length > 0 &&
      selectedOrganizations.length > 0
    ) {
      setTemplateDropdown(
        templateOverridesList.map((item) => ({
          label: item.name,
          value: item.template_overrides_id,
          body: item.body,
        }))
      );
    }
  }, [
    selectedPractitioners,
    templateOverridesList,
    refreshTrigger,
    selectedOrganizations.length,
  ]);

  useEffect(() => {
    if (selectedTemplates.length > 0) {
      setSelectedTemplatesDataSource((prevDataSource) => {
        const newTemplates = templateOverridesList
          .filter(
            (item) =>
              selectedTemplates.includes(item.template_overrides_id) &&
              !prevDataSource.some(
                (existing) =>
                  existing.template_overrides_id === item.template_overrides_id
              )
          )
          .map((item) => ({
            ...item,
            key: item.template_overrides_id,
          }));

        return [...prevDataSource, ...newTemplates];
      });
    } else {
      setSelectedTemplatesDataSource([]);
    }
  }, [
    templateOverridesList,
    selectedTemplates,
    refreshTrigger,
    setSelectedTemplatesDataSource,
  ]);

  return (
    <div className="flex-1">
      <div className="flex flex-col gap-1">
        <div className="text-lg font-bold font-sans pl-1 mb-2">Source</div>
        <Select
          showSearch={true}
          optionFilterProp="label"
          style={{ width: '100%' }}
          options={organizationDropdown}
          placeholder="Select organization"
          value={selectedOrganizations}
          onChange={(values) => setSelectedOrganizations(values)}
          className={classNames('font-sans w-full', 'single-select')}
        />
        <Select
          mode="multiple"
          showSearch={true}
          optionFilterProp="label"
          style={{ width: '100%' }}
          options={practitionerDropdown}
          placeholder="Select practitioner(s)"
          value={selectedPractitioners}
          onChange={(values) => {
            setSelectedPractitioners(values);
            if (templateOverridesList.length > 0) {
              const filteredTemplates = templateOverridesList.filter((item) =>
                values.includes(item.practitioner_id)
              );
              setTemplateDropdown(
                filteredTemplates.map((item) => ({
                  label: item.name,
                  value: item.template_overrides_id,
                  body: item.body,
                }))
              );
            }
          }}
          className={classNames('font-sans w-full', 'single-select')}
        />
        <Select
          mode="multiple"
          showSearch={true}
          optionFilterProp="label"
          style={{ width: '100%' }}
          options={templateDropdown}
          placeholder="Select template(s)"
          value={selectedTemplates}
          onChange={(values) => setSelectedTemplates(values)}
          className={classNames('font-sans w-full', 'single-select')}
          listHeight={300}
          optionRender={(option) => (
            <div className="flex items-center gap-2">
              <span>{option.data.label}</span>
              <Tooltip
                title={
                  <div
                    className={`overflow-auto cursor-pointer ${option.data.body.sections && option.data.body.sections.length > 10 ? 'max-h-[40rem]' : 'h-fit'} w-[15rem]`}
                  >
                    {option.data.body.sections &&
                    option.data.body.sections.length > 0 ? (
                      option.data.body.sections.map((section, index) => (
                        <p key={index}>
                          {section.name} <SummaryLoading />
                        </p>
                      ))
                    ) : (
                      <p>No template sections</p>
                    )}
                  </div>
                }
                placement={'right'}
                arrow={false}
                overlayStyle={
                  option.data.body.sections &&
                  option.data.body.sections.length > 10
                    ? { position: 'fixed', top: '1.25rem', bottom: '3rem' }
                    : {}
                }
              >
                <span style={{ cursor: 'default' }} className="text-gray-400">
                  <InfoCircleOutlined />
                </span>
              </Tooltip>
            </div>
          )}
        />
        {selectedTemplates.length > 0 &&
          selectedTemplatesDataSource.length > 0 && (
            <div className="mt-2">
              <Button
                type="primary"
                onClick={() => {
                  setSelectedTemplatesDataSource([]);
                  setSelectedTemplates([]);
                }}
              >
                Clear
              </Button>
              <div className="mt-2">
                <Table
                  columns={columns}
                  pagination={false}
                  bordered={true}
                  dataSource={selectedTemplatesDataSource}
                />
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default TemplatesAdminSource;
